import { type ComponentProps, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import {
  FilterableSelect,
  type SelectOption,
} from '../common/FilterableSelect';
import { ModelPicker, getProviderForModel } from '../ai/ModelPicker';
import { GenerationProgressIndicator } from '../ai/GenerationProgressIndicator';
import { useAIAction } from '../../hooks/useAIAction';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';
import { useAiModels } from '../../api/queries/aiModels';
import type { HierarchyPreset } from '../../api/queries/outlines';

type IndicatorEvent = ComponentProps<
  typeof GenerationProgressIndicator
>['event'];

interface GenerateOutlineDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

const HIERARCHY_OPTIONS: SelectOption[] = [
  { value: 'novel', label: 'Novel' },
  { value: 'screenplay', label: 'Screenplay' },
  { value: 'short_story', label: 'Short Story' },
  { value: 'series', label: 'Series' },
];

export function GenerateOutlineDialog({
  open,
  onClose,
  projectId,
}: GenerateOutlineDialogProps) {
  const action = useAIAction('generate-outline');
  const { data: models } = useAiModels();
  const [jobId, setJobId] = useState<string | null>(null);
  const { event } = useGenerationStatus(jobId);

  const [hierarchyPreset, setHierarchyPreset] =
    useState<HierarchyPreset>('novel');
  const [model, setModel] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  useEffect(() => {
    if (open) {
      setJobId(null);
      setHierarchyPreset('novel');
      setModel('');
      setCustomInstructions('');
    }
  }, [open]);

  const done = event?.status === 'complete';
  const failed = event?.status === 'failed';

  const handleSubmit = async () => {
    const provider = getProviderForModel(model, models) ?? 'anthropic';
    const result = await action.mutateAsync({
      project_id: projectId,
      hierarchy_preset: hierarchyPreset,
      provider,
      model,
      custom_instructions: customInstructions || undefined,
    });
    setJobId(result.jobId);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Generate outline</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FilterableSelect
            options={HIERARCHY_OPTIONS}
            value={hierarchyPreset}
            onChange={(v) =>
              setHierarchyPreset((v || 'novel') as HierarchyPreset)
            }
            label="Hierarchy preset"
            allowEmpty={false}
          />

          <ModelPicker value={model} onChange={setModel} />

          <TextField
            label="Custom instructions"
            placeholder="e.g., Focus on character arcs, include a prologue..."
            multiline
            minRows={3}
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
          />

          {jobId && (
            <GenerationProgressIndicator
              event={event as IndicatorEvent}
              onComplete={() => onClose()}
              onFailed={() => {}}
            />
          )}

          {failed && (
            <Alert severity="error">
              Generation failed:{' '}
              {(event?.payload as { error?: string } | undefined)?.error}
            </Alert>
          )}
          {done && (
            <Alert severity="success">Outline generated successfully.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={action.isPending || Boolean(jobId && !done && !failed)}
        >
          {action.isPending ? 'Submitting...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
